/**
 * Eyre ID layout config, served by OpenResty at /eyre-brand/ui.json (Fleet
 * apps/files/cn05/logto/branding/ui.json). Changing it needs no rebuild.
 *
 * {
 *   "default": { "layout": "plain" },
 *   "apps": { "<logto app id>": { "layout": "split", "panel": { "type": "video", "src": "/eyre-brand/x.mp4" } } },
 *   "links": { "help": "https://…", "privacy": "https://…", "terms": "https://…" }
 * }
 *
 * Panel types: image | video (same origin: CSP media-src is 'self') | iframe (any https page) | html (static markup).
 */
import { useEffect, useState } from 'react';

import { searchKeys } from '@/shared/utils/search-parameters';

export type Panel = {
  type: 'image' | 'video' | 'iframe' | 'html';
  src?: string;
  poster?: string;
  html?: string;
  alt?: string;
};

export type LayoutConfig = { layout: 'plain' | 'split'; panel?: Panel };

export type EyreUiConfig = {
  default?: LayoutConfig;
  apps?: Record<string, LayoutConfig>;
  links?: { help?: string; privacy?: string; terms?: string };
};

const previewKey = 'eyre_layout';

let configPromise: Promise<EyreUiConfig> | undefined;

const loadConfig = async (): Promise<EyreUiConfig> => {
  try {
    const response = await fetch('/eyre-brand/ui.json', { credentials: 'omit', cache: 'no-cache' });
    return response.ok ? await response.json() : {};
  } catch {
    return {};
  }
};

export const getAppId = () =>
  new URLSearchParams(window.location.search).get(searchKeys.appId) ??
  sessionStorage.getItem(searchKeys.appId) ??
  undefined;

/**
 * Preview override: cookie `eyre_layout=split|plain` (set by https://auth.nfeyre.com/_eyre_ui?split),
 * or `?eyre_layout=` on an experience URL for the rest of this tab session.
 */
const getPreviewLayout = (): LayoutConfig['layout'] | undefined => {
  const fromUrl = new URLSearchParams(window.location.search).get(previewKey);
  if (fromUrl === 'split' || fromUrl === 'plain') {
    sessionStorage.setItem(previewKey, fromUrl);
  }
  const fromCookie = /(?:^|;\s*)eyre_layout=(split|plain)/.exec(document.cookie)?.[1];
  const value = sessionStorage.getItem(previewKey) ?? fromCookie;
  return value === 'split' || value === 'plain' ? value : undefined;
};

export const useEyreUiConfig = () => {
  const [config, setConfig] = useState<EyreUiConfig>();

  useEffect(() => {
    configPromise ??= loadConfig();
    void configPromise.then(setConfig);
  }, []);

  return config;
};

export const resolveLayout = (config: EyreUiConfig | undefined): LayoutConfig => {
  const appId = getAppId();
  const base: LayoutConfig = (appId ? config?.apps?.[appId] : undefined) ??
    config?.default ?? { layout: 'plain' };
  const preview = getPreviewLayout();

  if (!preview) {
    return base;
  }

  return {
    layout: preview,
    panel:
      base.panel ??
      config?.default?.panel ??
      Object.values(config?.apps ?? {}).find(({ panel }) => panel)?.panel,
  };
};
